import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { Seller, SellerType, SellerStatus, FulfillmentCapability, AddSellerRequest, UpdateSellerRequest } from '../../../../api/shop/types/sellerTypes';
import { addSeller, updateSeller } from '../../../../api/shop';
import ShimmerLoader from '../../../../Common/ShimmerLoader';
import '../../../../Common/FormStyles.css';
import './SellerFormModal.css';

interface SellerFormModalProps {
  show: boolean;
  onHide: () => void;
  seller: Seller | null;
  onSuccess: () => void;
}

const SellerFormModal: React.FC<SellerFormModalProps> = ({
  show,
  onHide,
  seller,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddSellerRequest>({
    type: 'PLATFORM_OWNED',
    name: '',
    supportedFulfillment: [],
    status: 'ACTIVE',
  });

  useEffect(() => {
    if (seller) {
      // When editing, we need to include id for UpdateSellerRequest
      setFormData({
        type: seller.type,
        name: seller.name,
        supportedFulfillment: seller.supportedFulfillment || [],
        status: seller.status,
      });
    } else {
      // When adding, reset to default values (no id, no createdAt)
      setFormData({
        type: 'PLATFORM_OWNED',
        name: '',
        supportedFulfillment: [],
        status: 'ACTIVE',
      });
    }
    setError(null);
  }, [seller, show]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name: string, selectedOption: any) => {
    const value = selectedOption?.value || '';
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFulfillmentChange = (capability: FulfillmentCapability, checked: boolean) => {
    setFormData((prev) => {
      if (!checked) {
        return {
          ...prev,
          supportedFulfillment: (prev.supportedFulfillment || []).filter((c) => c !== capability),
        };
      }
      if (capability === 'EXTERNAL_ADS') {
        return { ...prev, supportedFulfillment: ['EXTERNAL_ADS'] };
      }
      const current = (prev.supportedFulfillment || []).filter((c) => c !== 'EXTERNAL_ADS');
      return { ...prev, supportedFulfillment: [...current, capability] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (seller) {
        // Update existing seller - include id from seller object
        const updatePayload: UpdateSellerRequest = {
          id: seller.id,
          type: formData.type,
          name: formData.name,
          supportedFulfillment: formData.supportedFulfillment,
          status: formData.status,
        };
        await updateSeller(seller._id, updatePayload);
      } else {
        // Add new seller - no id, backend will generate it
        await addSeller(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save seller. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setFormData({
      type: 'PLATFORM_OWNED',
      name: '',
      supportedFulfillment: [],
      status: 'ACTIVE',
    });
    onHide();
  };

  return (
    <Offcanvas 
      show={show} 
      onHide={handleClose} 
      placement="end" 
      className="seller-form-sidebar"
      backdrop="static"
      keyboard={false}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{seller ? 'Edit Seller' : 'Add New Seller'}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loading && !error ? (
          <ShimmerLoader type="form" />
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Seller Name - Full Width */}
            <Form.Group className="mb-3">
              <Form.Label>
                Seller Name<span className="required-asterisk">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Sports For Life"
              />
            </Form.Group>

            {/* Seller Type and Seller Status - Two Fields in One Row */}
            <div className="row mb-3">
              <div className="col-6">
                <Form.Group>
                  <Form.Label>
                    Seller Type<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={{
                      value: formData.type,
                      label: formData.type === 'PLATFORM_OWNED' ? 'Platform Owned' : 'Third Party'
                    }}
                    onChange={(selectedOption) => handleSelectChange('type', selectedOption)}
                    options={[
                      { value: 'PLATFORM_OWNED', label: 'Platform Owned' },
                      { value: 'THIRD_PARTY', label: 'Third Party' }
                    ]}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    maxMenuHeight={200}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group>
                  <Form.Label>
                    Seller Status<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={{
                      value: formData.status,
                      label: formData.status === 'ACTIVE' ? 'Active' : 'Inactive'
                    }}
                    onChange={(selectedOption) => handleSelectChange('status', selectedOption)}
                    options={[
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'INACTIVE', label: 'Inactive' }
                    ]}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    maxMenuHeight={200}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Fulfillment Capabilities */}
            <Form.Group className="mb-3">
              <Form.Label>
                Fulfillment Capabilities<span className="required-asterisk">*</span>
              </Form.Label>
              <div className="fulfillment-checkboxes">
                <Form.Check
                  type="checkbox"
                  id="fulfillment-pickup"
                  label="Pickup"
                  checked={formData.supportedFulfillment.includes('PICKUP')}
                  disabled={formData.supportedFulfillment.includes('EXTERNAL_ADS')}
                  onChange={(e) => handleFulfillmentChange('PICKUP', e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="fulfillment-delivery"
                  label="Delivery"
                  checked={formData.supportedFulfillment.includes('DELIVERY')}
                  disabled={formData.supportedFulfillment.includes('EXTERNAL_ADS')}
                  onChange={(e) => handleFulfillmentChange('DELIVERY', e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="fulfillment-external-ads"
                  label="External Ads"
                  checked={formData.supportedFulfillment.includes('EXTERNAL_ADS')}
                  onChange={(e) => handleFulfillmentChange('EXTERNAL_ADS', e.target.checked)}
                />
              </div>
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end mt-5">
              <Button variant="light" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading || formData.supportedFulfillment.length === 0}>
                {loading ? 'Saving...' : seller ? 'Update Seller' : 'Create Seller'}
              </Button>
            </div>
          </Form>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SellerFormModal;
